// tabs/CounseleesTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Avatar, Badge, Card, Center, Group, Loader, ActionIcon, Tooltip,
  SimpleGrid, Stack, Text, ThemeIcon,
} from '@mantine/core';
import { IconUsers, IconMail, IconPhone, IconRefresh } from '@tabler/icons-react';
import { useLeaderPortalService, useMemberService, CareCaseMember } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import {  MemberDetailModal } from '@/components';

interface CounseleesTabProps {
  caseID: number;
  active: boolean;
}

// Shape from searchMembers response data[]
interface Member {
  memberID: number;
  userId: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  dateOfBirth: string | null;
  gender: string | null;
  maritalStatusID: number;
  email: string;
  cellNumber: string | null;
  altContact: string | null;
  telephone: string | null;
  isMember: boolean;
  isActive: boolean;
  avatar: string | null;
  typeID: number;
  memberCode: string | null;
  modifiedDateTime: number;
  createdDateTime: number | null;
  // joined locally after enrichment
  isMain?: boolean;
  hasConsent?: boolean;
}

const fullName = (m: Member) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || `Member ${m.memberID}`;

const getInitials = (m: Member) =>
  [m.firstName?.[0], m.lastName?.[0]].filter(Boolean).join('').toUpperCase() || 'M';

// ── Counselee grid card (view-only, no remove action) ─────────────────────
function CounseleeCard({ member, onclick }: { member: Member; onclick: () => void }) {

  return (
    <Card withBorder radius="md" padding="lg" h="100%" onClick={onclick}>
      <Group justify="center" mb="sm">
        <Avatar src={member.avatar ?? undefined} alt={fullName(member)} size="xl" radius="md">
          {getInitials(member)}
        </Avatar>
      </Group>

      <Text fw={700} ta="center" size="md" mb={4} lineClamp={1}>
        {fullName(member)}
      </Text>

      {/* Main participant badge */}
      <Group justify="center" gap={6} mb={4}>
        {member.isMain && (
          <Badge size="sm" variant="light" color="blue">Main Participant</Badge>
        )}
      </Group>

      <Stack align="center" gap={2}>
        {member.email
          ? <Text size="sm" c="dimmed">Email: {member.email}</Text>
          : <Text size="sm" c="dimmed">No email on record</Text>
        }
        {(member.cellNumber || member.telephone || member.altContact)
          ? <Text size="sm" c="dimmed">Phone: {member.cellNumber ?? member.telephone ?? member.altContact}</Text>
          : <Text size="sm" c="dimmed">No phone on record</Text>
        }
      </Stack>
    </Card>
  );
}

function CounseleeCardSkeleton() {
  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm">
        <Avatar radius="xl" size={52} color="gray">M</Avatar>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Loading…</Text>
          <Text size="xs" c="dimmed">—</Text>
          <Text size="xs" c="dimmed">—</Text>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CounseleesTab({ caseID, active }: CounseleesTabProps) {
  const leaderPortal  = useLeaderPortalService();
  const memberService = useMemberService();

  const [counselees, setCounselees] = useState<Member[]>([]);
  const [loadingCounselees, setLoadingCounselees] = useState(false);   
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [modalOpened, setModalOpened] = useState(false);

  const handleCardClick = (member: Member) => {
    setSelectedMember(member);
    setModalOpened(true);
  };

  const fetchCounselees = useCallback(async () => {
    if (!caseID) return;
    setLoadingCounselees(true);
    try {
      // NOTE: the SDK method really is spelled with the double l. Everything user-facing in this
      // app uses "counselee" (matching `counseleeNames` on the view models), so do not "correct"
      // this call in a spelling sweep.
      const raw: any = await leaderPortal.counsellees(caseID);
      const members: CareCaseMember[] = raw?.data ?? raw ?? [];

      const enriched = await Promise.all(
        members.map(async (cm) => {
          try {
            const res: any = await memberService.searchMembers(String(cm.memberID));
            const list: Member[] = res?.data ?? res ?? [];
            const profile = list.find((m) => m.memberID === cm.memberID) ?? list[0];
            if (profile) return { ...profile, isMain: cm.isMain, hasConsent: cm.hasConsent };
          } catch { /* fall back silently */ }
          return {
            memberID: cm.memberID,
            userId: '',
            firstName: `Member`,
            middleName: null,
            lastName: String(cm.memberID),
            dateOfBirth: null,
            gender: null,
            maritalStatusID: 0,
            email: '',
            cellNumber: null,
            altContact: null,
            telephone: null,
            isMember: true,
            isActive: true,
            avatar: null,
            typeID: 0,
            memberCode: null,
            modifiedDateTime: 0,
            createdDateTime: null,
            isMain: cm.isMain,
            hasConsent: cm.hasConsent,
          } satisfies Member;
        })
      );

      setCounselees(enriched);
    } catch (e) {
      console.error('Error fetching counselees:', e);
      toast.error('Error', 'Could not load counselees.');
    } finally {
      setLoadingCounselees(false);
    }
  }, [caseID, leaderPortal, memberService]);

  useEffect(() => { if (active) fetchCounselees(); }, [active, fetchCounselees]);

  return (
    <Stack gap="lg">
      {/* ── Header: title + refresh only (no Add — leader is view-only) ── */}
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm" c="dimmed">
          Counselees on this case ({counselees.length})
        </Text>
        <Tooltip label="Refresh counselees" withArrow>
          <ActionIcon
            variant="subtle"
            onClick={fetchCounselees}
            loading={loadingCounselees}
            size="lg"
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* ── Counselees grid ── */}
      <Stack gap="xs">
        {loadingCounselees ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <CounseleeCardSkeleton />
            <CounseleeCardSkeleton />
            <CounseleeCardSkeleton />
          </SimpleGrid>
        ) : counselees.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={56} radius="xl" variant="light" color="teal">
                <IconUsers size={30} />
              </ThemeIcon>
              <Text fw={600}>No counselees yet</Text>
              <Text size="sm" c="dimmed" ta="center" maw={360}>
                No counselees have been added to this case yet.
              </Text>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {counselees.map((c) => (
              <CounseleeCard key={c.memberID} member={c} onclick={() => handleCardClick(c)} />
            ))}
          </SimpleGrid>
        )}
      </Stack>
      <MemberDetailModal
        member={selectedMember}
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        contextLabel="case"
      />
    </Stack>
  );
}